import { useState, useRef, useEffect } from "react";
import { Modal, Typography, IconButton, Button, InputBase } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import styles from "./SendFundsZapModal.module.scss";
import CustomLoader from "@/components/UI/CustomLoader/CustomLoader";
import SuccessfulSVG from "../../../../../assets/svg/SuccessfulSVG";
import TransferLogo from "@/assets/svg/TransferLogo";
import SearchRecipientSvg from "@/assets/svg/SearchRecipientSvg";
import CloseBtn from "@/assets/svg/CloseBtn";
import { useLazyGetUniversalSearchQuery } from "@/store/api/userApi";
import type { UserSearchItem } from "@/store/api/userApi";
import { useSendFundsMutation } from "@/store/api/walletApi";
import { formatMoney } from "@/helpers/formatMoney";

interface Props {
  onClose: () => void;
  maxAvailable?: number;
  initialRecipient?: UserSearchItem;
}

type Step = "form" | "verify" | "success";

const SendFundsZap = ({ onClose, maxAvailable = 1000, initialRecipient }: Props) => {
  const [amount, setAmount] = useState("");
  const [amountError, setAmountError] = useState("");
  const [step, setStep] = useState<Step>("form");

  const [recipientQuery, setRecipientQuery] = useState(
    initialRecipient ? `@${initialRecipient.username}` : ""
  );
  const [selectedRecipient, setSelectedRecipient] = useState<UserSearchItem | null>(
    initialRecipient ?? null
  );
  const [searchResults, setSearchResults] = useState<UserSearchItem[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const searchRef = useRef<HTMLDivElement>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [triggerSearch] = useLazyGetUniversalSearchQuery();
  const [sendFunds] = useSendFundsMutation();

  const isAmountValid = Number(amount) >= 1 && Number(amount) <= maxAvailable && !amountError;
  const isRecipientValid = !!selectedRecipient;
  const isFormValid = isAmountValid && isRecipientValid;
  const isSubmitting = step === "verify";
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleRecipientSearch = (value: string) => {
    setErrorMsg("");
    setRecipientQuery(value);
    setSelectedRecipient(null);

    if (!value.trim()) {
      setSearchResults([]);
      setDropdownOpen(false);
      return;
    }

    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(async () => {
      const q = value.replace(/^@/, "").trim();

      if (q.length < 2) {
        setDropdownOpen(false);
        return;
      }

      try {
        const result = await triggerSearch({
          query: q,
          type: "users",
          limit: 10,
        });

        if (result.data?.results) {
          const users = result.data.results.map((r) => r.data as UserSearchItem);
          setSearchResults(users);
          setDropdownOpen(true);
        }
      } catch (e) {
        console.error("Search error", e);
      }
    }, 400);
  };

  const handleSelectRecipient = (user: UserSearchItem) => {
    setSelectedRecipient(user);
    setRecipientQuery(`@${user.username}`);
    setDropdownOpen(false);
  };

  const handleClearRecipient = () => {
    setSelectedRecipient(null);
    setRecipientQuery("");
    setSearchResults([]);
  };

  const handleAmountChange = (value: string) => {
    const sanitized = value.replace(/[^0-9.]/g, "");
    setErrorMsg("");
    if (!sanitized) {
      setAmount("");
      setAmountError("");
      return;
    }

    const num = Number(sanitized);

    if (num > maxAvailable) return;

    setAmount(sanitized);

    if (num < 1) {
      setAmountError("Minimum transfer amount is $1.00");
    } else {
      setAmountError("");
    }
  };

  const handleSubmit = async () => {
    if (!isFormValid || !selectedRecipient) return;
    setErrorMsg("");
    setStep("verify");
    try {
      await sendFunds({
        recipientId: selectedRecipient.uid,
        amount: Number(amount),
      }).unwrap();
      setStep("success");
    } catch (e: any) {
      setErrorMsg(e?.data?.message ?? "Failed to send funds. Please try again.");
      setStep("form");
    }
  };

  const hasQuery = recipientQuery.trim().length > 0 && !selectedRecipient;
  const noResults = hasQuery && searchResults.length === 0;
  const hasResults = hasQuery && searchResults.length > 0;

  return (
    <Modal open onClose={isSubmitting ? undefined : onClose}>
      <div className={styles.overlay} onClick={isSubmitting ? undefined : onClose}>
        <div className={styles.modalWrapper} onClick={(e) => e.stopPropagation()}>
          {isSubmitting && (
            <>
              <div className={styles.blurOverlay} />
              <div className={styles.loaderOverlay}>
                <CustomLoader loading isModal={false} size={42} />
              </div>
            </>
          )}

          <div className={`${styles.modal} ${isSubmitting ? styles.modalBlurred : ""}`}>
            <div className={styles.header}>
              <Typography className={styles.title}>Send Funds</Typography>
              <IconButton onClick={onClose} className={styles.Btn} disabled={isSubmitting}>
                <CloseBtn />
              </IconButton>
            </div>

            {step === "form" && (
              <>
                <div className={styles.amoutBlock}>
                  <div className={styles.transferLogo}>
                    <TransferLogo />
                  </div>
                  <div>
                    <Typography className={styles.amoutTextHeader}>Internal Transfer</Typography>
                    <Typography className={styles.amoutText}>
                      Send funds instantly and with zero fees to <br /> any other user on the
                      platform.
                    </Typography>
                  </div>
                </div>

                <div className={styles.field} ref={searchRef}>
                  <Typography className={styles.label}>Find Recipient</Typography>

                  <div
                    className={`${styles.searchWrapper} ${
                      selectedRecipient ? styles.searchWrapperSelected : ""
                    }`}
                  >
                    {selectedRecipient ? (
                      selectedRecipient.image ? (
                        <img
                          src={selectedRecipient.image}
                          className={styles.selectedAvatarImg}
                          alt={selectedRecipient.username}
                        />
                      ) : (
                        <div className={styles.selectedAvatar}>
                          {selectedRecipient.username[0].toUpperCase()}
                        </div>
                      )
                    ) : (
                      <SearchRecipientSvg />
                    )}

                    <InputBase
                      placeholder="Search by username"
                      value={recipientQuery}
                      onChange={(e) => handleRecipientSearch(e.target.value)}
                      className={styles.searchInput}
                      disabled={isSubmitting}
                      readOnly={!!selectedRecipient}
                      onFocus={() => {
                        if (recipientQuery.trim() && !selectedRecipient) {
                          setDropdownOpen(true);
                        }
                      }}
                    />

                    {selectedRecipient && (
                      <IconButton
                        className={styles.clearBtn}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleClearRecipient();
                        }}
                        disabled={isSubmitting}
                        size="small"
                        disableRipple
                        disableFocusRipple
                      >
                        <CloseIcon className={styles.clearIcon} />
                      </IconButton>
                    )}
                  </div>

                  {dropdownOpen && (
                    <div className={styles.dropdownContainer}>
                      <div
                        className={`${styles.dropdown} ${
                          noResults ? styles.dropdownEmpty : styles.dropdownResults
                        }`}
                      >
                        {noResults && (
                          <div className={styles.noResults}>
                            No users found matching{" "}
                            <span className={styles.noResultsQuery}>
                              "{recipientQuery.replace(/^@/, "")}"
                            </span>
                          </div>
                        )}

                        {hasResults &&
                          searchResults.map((user) => (
                            <div
                              key={user.uid}
                              className={styles.dropdownItem}
                              onMouseDown={() => handleSelectRecipient(user)}
                            >
                              {user.image ? (
                                <img
                                  src={user.image}
                                  className={styles.userAvatarImg}
                                  alt={user.username}
                                />
                              ) : (
                                <div className={styles.userAvatar}>
                                  {user.username[0].toUpperCase()}
                                </div>
                              )}
                              <div className={styles.userInfo}>
                                <span className={styles.userUsername}>@{user.username}</span>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className={styles.field}>
                  <Typography className={styles.label}>Amount (USDC)</Typography>
                  <div
                    className={`${styles.amountWrapper} ${amountError ? styles.amountWrapperError : ""}`}
                  >
                    <span className={styles.currency}>$</span>
                    <InputBase
                      placeholder="0.00"
                      value={amount}
                      onChange={(e) => handleAmountChange(e.target.value)}
                      className={styles.amountInput}
                      inputMode="decimal"
                      type="number"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className={styles.maxBlock}>
                    <Typography className={styles.max}>
                      Max available: ${formatMoney(maxAvailable)}
                    </Typography>
                    {errorMsg && <Typography className={styles.amountError}>{errorMsg}</Typography>}
                  </div>
                </div>

                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={!isFormValid || isSubmitting}
                  className={`${styles.button} ${!isFormValid ? styles.buttonDisabled : ""}`}
                >
                  Send Funds
                </Button>
              </>
            )}

            {step === "success" && (
              <div className={styles.successContent}>
                <div className={styles.successIcon}>
                  <SuccessfulSVG />
                </div>
                <Typography className={styles.successTitle}>Funds Sent Successfully</Typography>
                <Typography className={styles.successDescription}>
                  Your transfer has been completed immediately. <br /> The recipient will see the
                  funds in their wallet.
                </Typography>
                <Button variant="contained" onClick={onClose} className={styles.buttonSuccess}>
                  Done
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default SendFundsZap;
